import {
  LAYER_COMPONENT_AUDIO,
  LAYER_COMPONENT_CAPTIONS,
  LAYER_COMPONENT_HTML_OVERLAY,
  LAYER_COMPONENT_VIDEO,
  type CompositionLayer,
  type Track,
  type TrackKind,
  type VideoComposition,
} from "./types";

/**
 * Which kind of track a layer belongs to when tracks are derived. Layers whose
 * component isn't listed — agent-generated custom components and the handful of
 * legacy component names still in stored compositions — fall back to
 * `DEFAULT_TRACK_KIND`.
 */
const COMPONENT_TRACK_KIND: Record<string, TrackKind> = {
  [LAYER_COMPONENT_VIDEO]: "video",
  [LAYER_COMPONENT_HTML_OVERLAY]: "overlay",
  [LAYER_COMPONENT_CAPTIONS]: "captions",
  [LAYER_COMPONENT_AUDIO]: "audio",
};

const DEFAULT_TRACK_KIND: TrackKind = "overlay";

const TRACK_KIND_LABEL: Record<TrackKind, string> = {
  video: "Video",
  overlay: "Overlay",
  captions: "Captions",
  audio: "Audio",
};

export function getLayerTrackKind(layer: CompositionLayer): TrackKind {
  return COMPONENT_TRACK_KIND[layer.component] ?? DEFAULT_TRACK_KIND;
}

/** The per-layer hidden flag that predates tracks (see `CompositionLayer`). */
export function isLayerHidden(layer: CompositionLayer): boolean {
  return (layer.props as { hidden?: boolean }).hidden === true;
}

/** The per-layer locked flag that predates tracks. Editor-only. */
export function isLayerLocked(layer: CompositionLayer): boolean {
  return (layer.props as { locked?: boolean }).locked === true;
}

/**
 * Whether a clip may be edited, given its own flag and its track's. Both levels
 * count: a clip locked before tracks existed stays locked, and locking a track
 * locks everything on it.
 */
export function isClipLocked(
  layer: CompositionLayer,
  track: Track | undefined,
): boolean {
  return isLayerLocked(layer) || track?.locked === true;
}

/** Display name for a track, falling back to its kind when unlabeled. */
export function getTrackLabel(track: Track): string {
  return track.label ?? TRACK_KIND_LABEL[track.kind];
}

/**
 * Whether two layers are ever on screen at the same time. Spans are half-open
 * — `[from, from + duration)` — so clips laid exactly end to end (the shape
 * `splitLayerAtFrame` produces) do not overlap and can share a track.
 */
export function layersOverlapInTime(
  a: CompositionLayer,
  b: CompositionLayer,
): boolean {
  return a.from < b.from + b.duration && b.from < a.from + a.duration;
}

export interface ResolvedTrack {
  track: Track;
  /** Clips on this track, in ascending `from` order. */
  clips: CompositionLayer[];
}

export interface ResolvedComposition {
  /** Bottom-first, matching `VideoComposition.tracks`. */
  tracks: ResolvedTrack[];
  /**
   * Every layer in paint order (bottom-first): by track, then by the layer's
   * original index within the track. Feed this to the renderer instead of
   * `composition.layers`.
   */
  paintOrder: CompositionLayer[];
  /** Track id per layer id, including layers that arrived unassigned. */
  trackIdByLayerId: Map<string, string>;
  /** True when no `tracks` were stored and the whole layout was derived. */
  derived: boolean;
}

/** A track under construction, plus the layers placed on it so far. */
interface Bin {
  track: Track;
  entries: { layer: CompositionLayer; index: number }[];
}

/**
 * Lowest bin index `layer` may occupy without changing what renders.
 *
 * Everything already placed sits below `layer` in the current z-order, so any
 * of those it overlaps in time must stay below it — meaning `layer` has to land
 * strictly above the highest such bin. Layers it never coexists with impose no
 * constraint at all, and that freedom is what lets sequential clips collapse
 * onto one track.
 */
function zOrderFloor(bins: Bin[], layer: CompositionLayer): number {
  let floor = 0;
  for (const [index, bin] of bins.entries()) {
    const conflicts = bin.entries.some((entry) =>
      layersOverlapInTime(entry.layer, layer),
    );
    if (conflicts) floor = index + 1;
  }
  return floor;
}

/**
 * What makes two layers eligible to share a derived track. Kind keeps unlike
 * content apart; the hidden and locked flags do too, because both are track-level
 * controls now — a hidden or locked layer sharing a lane with ordinary ones
 * could not be represented by the track's own flags, so its header would either
 * lie about the state or apply it to clips that never had it.
 */
interface TrackGroup {
  kind: TrackKind;
  hidden: boolean;
  locked: boolean;
}

function layerTrackGroup(layer: CompositionLayer): TrackGroup {
  return {
    kind: getLayerTrackKind(layer),
    hidden: isLayerHidden(layer),
    locked: isLayerLocked(layer),
  };
}

function trackMatchesGroup(track: Track, group: TrackGroup): boolean {
  return (
    track.kind === group.kind &&
    (track.hidden === true) === group.hidden &&
    (track.locked === true) === group.locked
  );
}

function createTrack(group: TrackGroup, ordinal: number): Track {
  return {
    id: `track-${group.kind}-${ordinal}`,
    kind: group.kind,
    // Carried up from the layers so the track header reflects the state a
    // pre-tracks composition already had, rather than reading as unset.
    ...(group.hidden && { hidden: true }),
    ...(group.locked && { locked: true }),
  };
}

/**
 * Place `layer` on the lowest track at or above `floor` that shares its group
 * and holds nothing overlapping it in time, appending a new track when none
 * qualifies. Returns the chosen bin index.
 */
function placeLayer(
  bins: Bin[],
  layer: CompositionLayer,
  index: number,
): number {
  const group = layerTrackGroup(layer);
  const floor = zOrderFloor(bins, layer);

  for (let i = floor; i < bins.length; i++) {
    const bin = bins[i]!;
    if (!trackMatchesGroup(bin.track, group)) continue;
    if (bin.entries.some((entry) => layersOverlapInTime(entry.layer, layer))) {
      continue;
    }
    bin.entries.push({ layer, index });
    return i;
  }

  const ordinal = bins.filter((bin) => bin.track.kind === group.kind).length;
  bins.push({
    track: createTrack(group, ordinal),
    entries: [{ layer, index }],
  });
  return bins.length - 1;
}

/**
 * Group a composition's layers into tracks.
 *
 * When `composition.tracks` is set it is authoritative: those tracks are used
 * as-is, in order, and each layer with a matching `trackId` stays where it was
 * put — even if the result overlaps or would pack differently today, because by
 * then it is the user's layout rather than ours. Layers without a usable
 * `trackId` are placed by the same rule used for a full derivation, which is
 * how a clip appended by the ad agent, an asset import, or the disclaimer
 * overlay joins an existing layout.
 *
 * When `composition.tracks` is absent the whole layout is derived by packing
 * layers bottom-up. Derivation never changes what renders: clips that never
 * coexist on screen have unobservable relative z-order (Remotion premounts at
 * `opacity: 0`, so premounted frames aren't observable either), and
 * `zOrderFloor` preserves the order of every pair that does coexist.
 *
 * Note which ordering governs depth in each case. Without stored tracks, the
 * `layers` array order is the z-order and derivation preserves it. With stored
 * tracks, *track* order is the z-order, and array order only breaks ties within
 * a single track — so an unassigned layer is placed by kind and time, and its
 * array position no longer constrains its depth. Callers that need a specific
 * depth for a layer they are adding should set `trackId` rather than relying on
 * where they splice it into `layers`.
 */
export function resolveTracks(
  composition: VideoComposition,
): ResolvedComposition {
  const stored = composition.tracks ?? [];
  const derived = stored.length === 0;
  const bins: Bin[] = stored.map((track) => ({ track, entries: [] }));
  const binByTrackId = new Map(stored.map((track, index) => [track.id, index]));
  const trackIdByLayerId = new Map<string, string>();

  // Strictly in array order, so `zOrderFloor`'s premise holds for every layer:
  // whatever is already binned sits below the one being placed. Pinning ahead
  // of time would break it, since a layer pinned by `trackId` can appear later
  // in the array — i.e. above — and must not push an earlier layer upward.
  for (const [index, layer] of composition.layers.entries()) {
    const pinned =
      layer.trackId != null ? binByTrackId.get(layer.trackId) : undefined;
    if (pinned != null) {
      bins[pinned]!.entries.push({ layer, index });
      trackIdByLayerId.set(layer.id, layer.trackId!);
      continue;
    }
    const binIndex = placeLayer(bins, layer, index);
    trackIdByLayerId.set(layer.id, bins[binIndex]!.track.id);
  }

  const paintOrder = bins.flatMap((bin) =>
    [...bin.entries]
      .sort((a, b) => a.index - b.index)
      .map((entry) => entry.layer),
  );

  const tracks = bins.map((bin) => ({
    track: bin.track,
    clips: [...bin.entries]
      .sort((a, b) => a.layer.from - b.layer.from)
      .map((entry) => entry.layer),
  }));

  return { tracks, paintOrder, trackIdByLayerId, derived };
}

/**
 * Normalize a composition's layout into stored form: write `tracks`, stamp every
 * layer's `trackId`, and drop tracks that hold no clips.
 *
 * Call this on the user's first edit of any kind — not merely on open, which
 * would rewrite every stored composition as a side effect of viewing it.
 *
 * Pruning lives here rather than in `resolveTracks`, which stays a faithful
 * projection of what's stored. A track empties whenever its last clip is
 * deleted or dragged away, and an empty row has no clips to act on and no way
 * to be removed — so it's normalized out on the next write instead of being
 * left as a dead lane. Because the editor materializes on read, that happens as
 * soon as the edit flows back rather than waiting for a save.
 */
export function materializeTracks(
  composition: VideoComposition,
): VideoComposition {
  const stored = composition.tracks;
  const occupied = new Set(composition.layers.map((layer) => layer.trackId));
  // Already stored, fully assigned, and no empty tracks: return the input
  // itself, not a copy. Callers materialize on every read to keep the layout
  // frozen, so an unchanged composition has to come back identical or it would
  // churn React identity — and look like an external edit to the rebase
  // detector.
  if (
    stored != null &&
    stored.length > 0 &&
    composition.layers.every(
      (layer) =>
        layer.trackId != null &&
        stored.some((track) => track.id === layer.trackId),
    ) &&
    stored.every((track) => occupied.has(track.id))
  ) {
    return composition;
  }

  const { tracks, trackIdByLayerId } = resolveTracks(composition);
  return {
    ...composition,
    tracks: tracks
      .filter((resolved) => resolved.clips.length > 0)
      .map((resolved) => resolved.track),
    layers: composition.layers.map((layer) => {
      const trackId = trackIdByLayerId.get(layer.id);
      return trackId == null || layer.trackId === trackId
        ? layer
        : { ...layer, trackId };
    }),
  };
}
